import React, { useState, useEffect } from 'react';
import { createTeamMember, updateTeamMember, getCategories } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const TeamForm = ({ editingItem, onClose, onRefresh }) => {
  const [teamCategories, setTeamCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    position: editingItem?.position || '',
    department: editingItem?.department || '',
    bio: editingItem?.bio || '',
    email: editingItem?.email || '',
    linkedin: editingItem?.linkedin || '',
    twitter: editingItem?.twitter || '',
    photo: editingItem?.photo || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const { imageFile, uploading, preview, handleImageChange, uploadImage } = useImageUpload();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      // Filter only team type categories
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'team');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'team');
      }
      
      setTeamCategories(categoriesArray);
      
      // Set default department if editing and category exists
      if (editingItem?.department && categoriesArray.length > 0 && !formData.department) {
        const existingCategory = categoriesArray.find(c => c.name === editingItem.department);
        if (existingCategory) {
          setFormData(prev => ({ ...prev, department: existingCategory.name }));
        }
      } else if (!editingItem && categoriesArray.length > 0 && !formData.department) {
        // Set first category as default for new form
        setFormData(prev => ({ ...prev, department: categoriesArray[0].name }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://inteliheads.in/api'}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'team',
          order: teamCategories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Department added successfully');
        setTeamCategories([...teamCategories, { _id: result.data._id, name: newCategory.trim(), type: 'team' }]);
        setFormData({ ...formData, department: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add department');
      }
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error('Error adding department');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let photo = formData.photo;
    if (imageFile) photo = await uploadImage();
    
    const submitData = {
      name: formData.name,
      position: formData.position,
      department: formData.department,
      bio: formData.bio,
      email: formData.email,
      linkedin: formData.linkedin,
      twitter: formData.twitter,
      photo: photo,
      order: parseInt(formData.order),
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateTeamMember(editingItem._id, submitData);
        toast.success('Team member updated successfully');
      } else {
        await createTeamMember(submitData);
        toast.success('Team member created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving team member:', error);
      toast.error(error.message || 'Error saving team member');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = teamCategories.find(c => c.name === categoryName);
    return category?.icon || '👥';
  };

  if (loadingCategories) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem' }}>Loading departments...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Team Member' : 'Add New Team Member'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Name and Position - First Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Full Name *" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            <FormInput label="Position *" name="position" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required />
          </div>
          
          {/* Department/Category Selection - Second Row (From Database) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Department *</label>
            <select
              required
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
            >
              <option value="">-- Select a department --</option>
              {teamCategories.length === 0 ? (
                <option value="">No departments available. Please add a department first.</option>
              ) : (
                teamCategories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.icon} {cat.name}
                  </option>
                ))
              )}
            </select>
            
            {!showNewCategoryInput ? (
              <button 
                type="button"
                onClick={() => setShowNewCategoryInput(true)}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: 'transparent',
                  color: '#2C7A7B',
                  border: '1px solid #2C7A7B',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <i className="fas fa-plus"></i> Add New Department
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new department name"
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddNewCategory}
                  style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategory('');
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
            
            {formData.department && teamCategories.length > 0 && (
              <small style={{ color: '#2C7A7B', display: 'block', marginTop: '0.25rem' }}>
                <i className="fas fa-info-circle"></i> Selected: {getCategoryIcon(formData.department)} {formData.department}
              </small>
            )}
          </div>
          
          {/* Email and Social Links - Third Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Email" name="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <FormInput label="LinkedIn URL" name="linkedin" value={formData.linkedin} onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })} />
          </div>
          
          {/* Twitter and Order */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Twitter URL" name="twitter" value={formData.twitter} onChange={(e) => setFormData({ ...formData, twitter: e.target.value })} />
            <FormInput label="Display Order" name="order" type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} />
          </div>
          
          {/* Bio */}
          <FormInput label="Bio" name="bio" type="textarea" rows="4" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} />
          
          {/* Profile Photo */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Profile Photo</label>
            {preview && <img src={preview} alt="Preview" style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '0.5rem', display: 'block', objectFit: 'cover' }} />}
            {formData.photo && !preview && <img src={getFullImageUrl(formData.photo)} alt="Current" style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '0.5rem', display: 'block', objectFit: 'cover' }} />}
            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e.target.files[0])} style={{ width: '100%', marginBottom: '0.5rem' }} />
            <input 
              type="text" 
              value={formData.photo} 
              onChange={(e) => setFormData({ ...formData, photo: e.target.value })} 
              placeholder="Photo URL" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '0.5rem' }} 
            />
          </div>
          
          {/* Active Status */}
          <FormInput label="Active" name="isActive" type="checkbox" value={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" disabled={loading || uploading} style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {loading || uploading ? 'Saving...' : 'Save Team Member'}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamForm;